import type { Workspace } from "@kritaka/adapter";
import { listChannels, type DataContext } from "@kritaka/core";
import { useDataContext } from "@kritaka/ui";
import { useEffect, useMemo, useState } from "react";

const POLL_INTERVAL_MS = 30_000;

export interface PendingDecisionsByWorkspaceState {
  pendingByWorkspace: Record<string, number>;
  total: number;
}

/**
 * Pending-decisions counts for every workspace the user has access to —
 * the data backing the workspace-tile aggregate badge and the cross-
 * workspace `(N) Kritaka` document.title prefix (KTK-260).
 *
 * Live-update strategy:
 * - `decisionsRefetchKey` re-fires the per-workspace fetch on every
 *   `decision_*` relay broadcast for the active workspace, so the active
 *   tile + tab title pick up changes within one round-trip. The relay is
 *   workspace-scoped, so other workspaces don't broadcast into this tab —
 *   they catch up on the polling interval below.
 * - `POLL_INTERVAL_MS` keeps inactive workspaces fresh enough that the
 *   browser tab title is right when the user is multi-tasking across
 *   workspaces. 30s is far below the human attention threshold for the
 *   tab-title cue and far above any reasonable decision-creation rate.
 *
 * Source of truth is `pending_decisions_count` joined into the channels-
 * list response — same field driving the per-channel badges.
 */
export function usePendingDecisionsByWorkspace(
  decisionsRefetchKey: number,
  workspaceReloadToken: number,
): PendingDecisionsByWorkspaceState {
  const ctx = useDataContext();
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [pendingByWorkspace, setPendingByWorkspace] = useState<Record<string, number>>({});

  // Workspace list — refreshes when a workspace is added/renamed (the same
  // `workspaceReloadToken` Shell already bumps for the WorkspaceList itself).
  useEffect(() => {
    let cancelled = false;
    ctx.hub
      .listWorkspaces()
      .then((ws) => {
        if (!cancelled) setWorkspaces(ws);
      })
      .catch(() => {
        // Hub blip — keep the previous list and let polling rescue us.
      });
    return () => {
      cancelled = true;
    };
  }, [ctx.hub, workspaceReloadToken]);

  // Per-workspace counts. Refresh on relay decision events (active
  // workspace) and on a 30s interval (covers everything else).
  useEffect(() => {
    if (!workspaces || workspaces.length === 0) return;

    let cancelled = false;
    const dataCtx = ctx as unknown as DataContext;

    async function refresh() {
      const entries = await Promise.all(
        (workspaces ?? []).map(async (w) => {
          try {
            const cs = await listChannels(dataCtx, w.id);
            const sum = cs.reduce((n, c) => n + (c.pending_decisions_count ?? 0), 0);
            return [w.id, sum] as const;
          } catch {
            // Per-workspace failure shouldn't blank the whole map; fall back
            // to the previous value on next setState below.
            return [w.id, undefined] as const;
          }
        }),
      );
      if (cancelled) return;
      setPendingByWorkspace((prev) => {
        const next = { ...prev };
        for (const [id, sum] of entries) {
          if (sum !== undefined) next[id] = sum;
        }
        return next;
      });
    }

    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [ctx, workspaces, decisionsRefetchKey]);

  const total = useMemo(
    () => Object.values(pendingByWorkspace).reduce((a, b) => a + b, 0),
    [pendingByWorkspace],
  );

  return { pendingByWorkspace, total };
}
